/**
 * Quiet sounds under the claim haptics (SCORZA-110): a low thud that gives the
 * buzz some body, a wooden knock for a hat-trick or claiming every goal, and a
 * music box under a Save splash.
 * Synthesised, so there are no audio files to ship.
 */
let context: AudioContext | undefined;

const audio = () => {
    if (!context) {
        // Ambient: obeys the silent switch and mixes with the user's music
        const session = (navigator as { audioSession?: { type: string } })
            .audioSession;
        if (session) session.type = "ambient";
        context = new AudioContext();
    }
    // iOS starts a context suspended until a tap; every sound follows one
    if (context.state === "suspended") context.resume();
    return context;
};

interface Note {
    type?: OscillatorType;
    delay?: number;
    volume: number;
    attack?: number;
    // Time from the peak until it has faded out
    release: number;
    // Start this far above the note and drop onto it, for a hit
    drop?: number;
}

const note = (
    hz: number,
    { type = "sine", delay = 0, volume, attack = 0.006, release, drop }: Note
) => {
    const ctx = audio();
    const start = ctx.currentTime + delay;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(drop ? hz * drop : hz, start);
    if (drop) osc.frequency.exponentialRampToValueAtTime(hz, start + 0.05);
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(volume, start + attack);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + attack + release);
    osc.connect(gain).connect(ctx.destination);
    osc.start(start);
    osc.stop(start + attack + release);
};

// A soft sine hit whose pitch drops as it fades, like a ball on a hand
const thud = (hz: number, volume: number, seconds = 0.14) =>
    note(hz, { volume, release: seconds - 0.006, drop: 1.6 });

export const goalSound = (goalsAfter: number) =>
    thud(95, goalsAfter >= 2 ? 0.22 : 0.16);

export const assistSound = () => thud(150, 0.1, 0.1);

// The third claim: three dry, marimba-like knocks rising (C, E, G) over a thud
export const hatTrickSound = () => {
    thud(95, 0.18);
    [523, 659, 784].forEach((hz, i) =>
        note(hz, {
            type: "triangle",
            delay: 0.05 + i * 0.09,
            volume: 0.07,
            attack: 0.003,
            release: 0.12,
        })
    );
};

// Under a Save splash: four rounded notes rising (C E G C), like a music box.
// A faint octave on each gives the sparkle without a hard edge.
export const splashSound = () =>
    [523, 659, 784, 1047].forEach((hz, i) => {
        const delay = 0.03 + i * 0.1;
        note(hz, { delay, volume: 0.045, attack: 0.012, release: 0.9 });
        note(hz * 2, { delay, volume: 0.008, attack: 0.012, release: 0.4 });
    });

export const refusedSound = () => thud(70, 0.12, 0.2);

export const releasedSound = () => thud(120, 0.07, 0.09);
